/**
 * Orquesta sync Modules (legacy) → Django (estudyoo-sync).
 * Fase 1: lee paquete legacy (1 request).
 * Fase 2: upserts a Django en lotes bulk secuenciales (un POST por lote, sin solapamiento).
 */
import SyncModules from '@/services/SyncModulesService';
import * as DjangoSync from '@/services/DjangoSyncService';

const ENT = {
  COLEGIO: 'colegio',
  PERIODO: 'periodo',
  CURSO: 'curso',
  ASIGNATURA: 'asignatura',
  EJE: 'eje',
  CAPITULO: 'capitulo',
  ESTUDIANTE: 'estudiante',
  CONTRATO: 'contrato',
  DETALLE_CONTRATO: 'detalle_contrato',
  PREGUNTA: 'pregunta',
  ALTERNATIVA: 'alternativa',
};

const LIMITS = {
  colegio: { nombre: 255, rbd: 20, comuna: 120, region: 120 },
};

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/** Registros por lote en POST /upsert/bulk/ (debe coincidir con Django). */
export const BATCH_SIZE_SYNC_OPTS = [10, 20, 30, 40, 50, 60, 70, 80, 90];
export const BATCH_SIZE_SYNC_DEFAULT = 50;

/** Modo de POST /sesion/{id}/evento/ durante la sync. */
export const EVENTOS_SYNC_MODOS = [
  { value: 'completo', label: 'Completo — cada paso se guarda en Django' },
  { value: 'solo_errores', label: 'Solo errores — fallos y reconciliación con error' },
  { value: 'off', label: 'Desactivado — log solo en pantalla (más rápido)' },
];
export const EVENTOS_SYNC_STORAGE_KEY = 'estudyoo_sync_eventos_modo';
export const EVENTOS_SYNC_DEFAULT = 'off';

export function normalizarModoEventosSync(modo) {
  const v = String(modo || '').trim();
  if (EVENTOS_SYNC_MODOS.some((m) => m.value === v)) return v;
  return EVENTOS_SYNC_DEFAULT;
}

export function leerModoEventosSync() {
  try {
    return normalizarModoEventosSync(localStorage.getItem(EVENTOS_SYNC_STORAGE_KEY));
  } catch {
    return EVENTOS_SYNC_DEFAULT;
  }
}

export function guardarModoEventosSync(modo) {
  try {
    localStorage.setItem(EVENTOS_SYNC_STORAGE_KEY, normalizarModoEventosSync(modo));
  } catch {
    /* ignore */
  }
}

function debePersistirEventoSync(modoEventos, { error }) {
  const modo = normalizarModoEventosSync(modoEventos);
  if (modo === 'off') return false;
  if (modo === 'solo_errores') return !!error;
  return true;
}

/** Lanzada cuando el usuario detiene la sync desde el admin. */
export class SyncAbortError extends Error {
  constructor(message = 'Sync cancelada por el usuario') {
    super(message);
    this.name = 'SyncAbortError';
  }
}

function assertNotAborted(signal) {
  if (signal?.aborted) throw new SyncAbortError();
}

/** Slug de casa/sección legacy (Lanester, A, B…) — alineado con Django sync_handlers. */
function slugCasa(casa) {
  const c = String(casa || '').trim();
  if (!c) return '';
  if (c.length === 1 && /[A-Za-z]/.test(c)) return c.toUpperCase();
  return c
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || c.toLowerCase().slice(0, 40);
}

function cursoCasaAlumno(u) {
  return String(u?.Curso_casa || u?.curso_casa || u?.casa || '').trim();
}

function idExternoCursoSeccion(idCurso, casa) {
  const base = String(idCurso ?? '').trim();
  const slug = slugCasa(casa);
  return slug ? `${base}-${slug}` : base;
}

function normalizarBatchSize(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 1) return BATCH_SIZE_SYNC_DEFAULT;
  const opts = BATCH_SIZE_SYNC_OPTS;
  const capped = Math.min(opts[opts.length - 1], Math.max(1, Math.floor(v)));
  return opts.includes(capped) ? capped : opts.reduce((best, o) => (
    Math.abs(o - capped) < Math.abs(best - capped) ? o : best
  ), opts[0]);
}

function chunk(arr, size) {
  const n = arr?.length || 0;
  if (!n) return [];
  const sz = normalizarBatchSize(size);
  const out = [];
  for (let i = 0; i < n; i += sz) out.push(arr.slice(i, i + sz));
  return out;
}

function faseVacia() {
  return { ok: 0, error: 0, creado: 0, actualizado: 0, omitido: 0, stale: 0 };
}

function contarResultado(bucket, r) {
  if (!r?.ok) return;
  if (r.omitido || r.accion === 'omitido') bucket.omitido += 1;
  else if (r.created || r.accion === 'creado') bucket.creado += 1;
  else bucket.actualizado += 1;
  bucket.ok += 1;
}

/** Extrae texto de string o objeto legacy { Glosa, Nombre, ... }. */
export function textoLegacy(val, maxLen = null) {
  if (val == null) return '';
  if (typeof val === 'object') {
    val = val.Glosa || val.Nombre || val.nombre || val.glosa || val.Codigo || '';
  }
  const s = String(val).trim();
  return maxLen != null && s.length > maxLen ? s.slice(0, maxLen) : s;
}

/** Payload mínimo para upsert colegio — sin objetos Comuna/Region anidados. */
export function payloadColegioDjango(raw) {
  if (!raw) return {};
  const calle = raw.DireccionCalle || raw.direccion_calle || '';
  const num = raw.DireccionNumero || raw.direccion_numero || '';
  const dir = [calle, num].filter(Boolean).join(' ').trim();
  return {
    Id: raw.Id ?? raw.id,
    Glosa: textoLegacy(raw.Glosa || raw.Nombre, LIMITS.colegio.nombre),
    IdentificadorRBD: textoLegacy(raw.IdentificadorRBD || raw.Rbd || raw.rbd, LIMITS.colegio.rbd),
    comuna: textoLegacy(raw.comuna ?? raw.Comuna, LIMITS.colegio.comuna),
    region: textoLegacy(raw.region ?? raw.Region, LIMITS.colegio.region),
    direccion: textoLegacy(raw.direccion ?? raw.Direccion ?? dir, 500),
    Vigente: raw.Vigente !== false,
  };
}

/** @deprecated use payloadColegioDjango */
export function normalizarColegio(raw) {
  return payloadColegioDjango(raw);
}

function sanitizeDatos(entidad, datos) {
  if (!datos || typeof datos !== 'object') return datos;
  if (entidad === ENT.COLEGIO) return payloadColegioDjango(datos);
  const out = { ...datos };
  for (const [k, v] of Object.entries(out)) {
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = textoLegacy(v, 500);
    }
  }
  return out;
}

/** Construye fila de log por objeto sincronizado. */
export function buildSyncLogEntry({ fase, entidad, item, result, error, tipo = 'upsert' }) {
  const id = item?.Id ?? item?.id_usuario ?? item?.id ?? item?.id_pregunta ?? '—';
  if (tipo === 'info') {
    return {
      fase: fase || '—',
      entidad: entidad || fase || '—',
      accion: 'info',
      ok: true,
      id: '—',
      etiqueta: '',
      mensaje: item?.mensaje || result?.mensaje || '',
      cambios: [],
      djangoId: null,
    };
  }
  if (error) {
    const errData = error.response?.data;
    const msg = errData?.error || errData?.detail || error.message;
    return {
      fase: fase || entidad || '—',
      entidad: errData?.entidad || result?.entidad || entidad || fase,
      accion: 'error',
      ok: false,
      id: String(id),
      etiqueta: item?.Glosa || item?.Nombre || '',
      mensaje: typeof msg === 'string' ? msg : JSON.stringify(msg),
      cambios: [],
      djangoId: null,
    };
  }
  const det = result?.detalle || {};
  const accion = det.accion || result?.accion || 'ok';
  return {
    fase: fase || entidad || '—',
    entidad: result?.entidad || entidad || fase,
    accion,
    ok: result?.ok !== false,
    id: String(result?.id_externo || id),
    etiqueta: det.etiqueta || item?.Glosa || item?.Nombre || '',
    mensaje: det.mensaje || (accion === 'omitido' ? 'Sin cambios' : String(accion)),
    cambios: det.cambios || [],
    djangoId: result?.django_id || det.django_id || null,
  };
}

function reportReconciliar(report, fase, entidad, rec) {
  const stale = rec?.stale || [];
  report(fase, { reconciliar: true }, {
    ok: true,
    entidad,
    accion: 'reconciliar',
    id_externo: '—',
    detalle: {
      accion: 'reconciliar',
      etiqueta: `Reconciliación ${entidad}`,
      mensaje: stale.length
        ? `${stale.length} registro(s) ya no vienen del legacy`
        : 'Sin registros obsoletos',
      cambios: stale.map((ie) => ({
        campo: 'stale',
        anterior: ie,
        nuevo: null,
      })),
    },
  }, null);
}

/** Persiste en Django eventos que el navegador vería pero el servidor no registró (red, info). */
async function persistirEventoSync(sesionId, { fase, entidad, item, error, tipo, result }, modoEventos) {
  if (!sesionId) return;
  if (!debePersistirEventoSync(modoEventos, { error })) return;
  if (error?.response?.status === 400 && error.response?.data?.error) {
    return;
  }
  const id = item?.Id ?? item?.id ?? item?.id_usuario ?? item?.id_pregunta ?? '—';
  const ent = entidad || fase || 'colegio';
  let mensaje = '';
  if (error) {
    const d = error.response?.data;
    mensaje = (typeof d === 'string' ? d : d?.error || d?.detail) || error.message || 'Error';
  } else {
    mensaje = item?.mensaje || result?.mensaje || result?.detalle?.mensaje || '';
  }
  try {
    await DjangoSync.registrarEvento(sesionId, {
      evento: {
        entidad: ent,
        id_externo: String(id),
        accion: error ? 'error' : (tipo === 'info' ? 'info' : 'info'),
        ok: !error,
        mensaje: String(mensaje).slice(0, 1900),
        fase: fase || '',
        fuente: 'cliente',
        detalle: error?.response?.data
          ? { respuesta: error.response.data }
          : (result?.detalle ? { detalle: result.detalle } : {}),
      },
    });
  } catch {
    /* no interrumpir sync */
  }
}

function crearReporter(sesionId, onProgress, modoEventos) {
  return ({ fase, item, result, error, tipo, entidad, ...extra }) => {
    persistirEventoSync(sesionId, { fase, entidad, item, error, tipo, result }, modoEventos);
    onProgress({ fase, item, result, error, tipo, entidad, ...extra });
  };
}

/** Mapa eje/unidad legacy → asignatura (desde capítulos del paquete). */
function mapaEjeAsignatura(paquete) {
  const m = {};
  for (const cap of paquete.capitulos || []) {
    const eid = cap.IdEjeTematico ?? cap.idEjeTematico ?? cap.IdUnidad ?? cap.idUnidad;
    const aid = cap.IdAsignatura ?? cap.idAsignatura;
    if (eid && aid) m[String(eid)] = aid;
  }
  return m;
}

/** Asegura IdEjeTematico y lista de ejes a partir de unidad_id (como chapters-subject del alumno). */
function enriquecerEjesDesdeCapitulos(paquete) {
  if (!paquete?.capitulos?.length) return paquete;
  const ejesPorId = indiceEjes(paquete);
  for (const cap of paquete.capitulos) {
    const uid = cap.IdUnidad ?? cap.idUnidad ?? cap.IdEjeTematico ?? cap.idEjeTematico;
    if (!uid) continue;
    const key = String(uid);
    if (!cap.IdEjeTematico && !cap.idEjeTematico) cap.IdEjeTematico = uid;
    if (!cap.IdUnidad && !cap.idUnidad) cap.IdUnidad = uid;
    if (!ejesPorId[key]) {
      const glosa = (cap.UnidadGrupo || cap.unidadGrupo || '').trim();
      ejesPorId[key] = {
        Id: uid,
        Glosa: glosa || `Unidad ${uid}`,
        Vigente: true,
      };
    }
  }
  paquete.ejes = Object.values(ejesPorId).sort((a, b) => Number(a.Id) - Number(b.Id));
  if (paquete.meta) paquete.meta.total_ejes = paquete.ejes.length;
  return paquete;
}

async function upsertOne(sesionId, entidad, datos, refs, colegioIdExterno, forzarActualizacion = false) {
  const limpio = sanitizeDatos(entidad, datos);
  const { data } = await DjangoSync.upsert({
    sesion_id: sesionId,
    entidad,
    datos: limpio,
    refs: refs || {},
    colegio_id_externo: String(colegioIdExterno || ''),
    id_externo: limpio?.Id != null ? String(limpio.Id) : limpio?.id_externo,
    forzar: !!forzarActualizacion,
  });
  if (data?.ok === false) {
    const err = new Error(data.error || 'Error upsert');
    err.response = { data };
    throw err;
  }
  return data;
}

function logItemsBulkDesdeModo(modoEventos) {
  return normalizarModoEventosSync(modoEventos) === 'completo';
}

/** Configurado al iniciar ejecutarSincronizacionCompleta. */
let _syncLogItemsBulk = true;

async function upsertBulk(sesionId, specs, colegioIdExterno, forzarActualizacion = false, logItems = true) {
  const items = specs.map((spec) => {
    const limpio = sanitizeDatos(spec.entidad, spec.datos);
    return {
      entidad: spec.entidad,
      datos: limpio,
      refs: spec.refs || {},
      id_externo: spec.id_externo != null
        ? String(spec.id_externo)
        : (limpio?.Id != null ? String(limpio.Id) : limpio?.id_externo),
    };
  });
  const payload = {
    colegio_id_externo: String(colegioIdExterno || ''),
    forzar: !!forzarActualizacion,
    log_items: !!logItems,
    items,
  };
  if (sesionId) payload.sesion_id = sesionId;
  const { data } = await DjangoSync.upsertBulk(payload);
  return data;
}

/**
 * Sincroniza una lista en lotes secuenciales hacia POST /upsert/bulk/.
 * Cada lote = 1 request con todos los ítems del chunk en payload.items[].
 * Espera la respuesta del backend antes de enviar el siguiente lote (sin solapamiento).
 */
async function syncBulkLista({
  sesionId,
  cid,
  forzarActualizacion,
  lista,
  bucket,
  fase,
  entidad,
  tick,
  report,
  batchSize,
  onItemSuccess,
  signal,
  logItemsBulk: logItemsBulkIn,
}) {
  const logItemsBulk = logItemsBulkIn ?? _syncLogItemsBulk;
  const chunks = chunk(lista, batchSize);
  if (!chunks.length) return;
  const entLabel = entidad || lista[0]?.entidad || fase;
  const reportCompacto = !logItemsBulk;

  for (let batchIdx = 0; batchIdx < chunks.length; batchIdx += 1) {
    assertNotAborted(signal);
    const batch = chunks[batchIdx];
    tick(
      fase || entLabel,
      `Lote ${batchIdx + 1}/${chunks.length} · ${batch.length} ${entLabel}(s) → Django`,
      entLabel,
    );
    try {
      const data = await upsertBulk(sesionId, batch, cid, forzarActualizacion, logItemsBulk);
      const results = data?.results || [];
      if (reportCompacto) {
        let okN = 0;
        let errN = 0;
        for (let i = 0; i < batch.length; i += 1) {
          const spec = batch[i];
          const r = results[i] || { ok: false, error: 'sin respuesta en lote' };
          if (r.ok) {
            okN += 1;
            contarResultado(bucket, r);
            if (onItemSuccess) onItemSuccess(spec);
          } else {
            errN += 1;
            bucket.error += 1;
            const err = new Error(r.error || 'Error en lote bulk');
            err.response = { data: r };
            report(spec.fase || fase || entLabel, spec.item || spec.datos, r, err);
          }
        }
        if (okN > 0) {
          report(
            fase || entLabel,
            { mensaje: `Lote ${batchIdx + 1}/${chunks.length}: ${okN} ok${errN ? `, ${errN} error(es)` : ''}` },
            { ok: true, resumen: data?.resumen },
            null,
            { tipo: 'info' },
          );
        }
      } else {
        for (let i = 0; i < batch.length; i += 1) {
          const spec = batch[i];
          const r = results[i] || { ok: false, error: 'sin respuesta en lote' };
          if (r.ok) {
            contarResultado(bucket, r);
            report(spec.fase || fase || entLabel, spec.item || spec.datos, r, null);
            if (onItemSuccess) onItemSuccess(spec);
          } else {
            bucket.error += 1;
            const err = new Error(r.error || 'Error en lote bulk');
            err.response = { data: r };
            report(spec.fase || fase || entLabel, spec.item || spec.datos, r, err);
          }
        }
      }
    } catch (e) {
      for (const spec of batch) {
        bucket.error += 1;
        report(spec.fase || fase || entLabel, spec.item || spec.datos, null, e);
      }
    }
  }
}

async function reconciliarEntidad(sesionId, entidad, ids, colegioId) {
  const { data } = await DjangoSync.reconciliar({
    sesion_id: sesionId,
    entidad,
    colegio_id_externo: String(colegioId),
    ids_presentes: ids.map((x) => String(x)),
  });
  return data;
}

/** Índice de ejes del paquete legacy (Id → fila). */
function indiceEjes(paquete) {
  const m = {};
  for (const e of paquete.ejes || []) {
    m[String(e.Id)] = e;
  }
  return m;
}

/** Referencias curso / asignatura / eje para upsert de capítulo. */
function refsCapitulo(cap, ejePorId) {
  const ejeId = cap.IdEjeTematico || cap.idEjeTematico || cap.IdUnidad || cap.idUnidad;
  const eje = ejeId ? ejePorId[String(ejeId)] : null;
  return {
    eje_id: ejeId,
    asignatura_id: cap.IdAsignatura ?? cap.idAsignatura ?? eje?.IdAsignatura ?? eje?.idAsignatura,
    curso_id: cap.IdCurso ?? cap.idCurso,
  };
}

/** Árbol curso → materia → eje → capítulos (orden legacy). */
function agruparCatalogoPorCurso(paquete) {
  const asigPorId = {};
  for (const a of paquete.asignaturas || []) {
    asigPorId[String(a.Id)] = a;
  }
  const ejePorId = indiceEjes(paquete);
  const caps = paquete.capitulos || [];

  return (paquete.cursos || []).map((cur) => {
    const capsCur = caps.filter(
      (c) => String(c.IdCurso ?? c.idCurso ?? '') === String(cur.Id),
    );
    const asigIds = [
      ...new Set(
        capsCur
          .map((c) => String(c.IdAsignatura ?? c.idAsignatura ?? ''))
          .filter(Boolean),
      ),
    ].sort((a, b) => Number(a) - Number(b));

    const materias = asigIds.map((aid) => {
      const capsAsig = capsCur.filter(
        (c) => String(c.IdAsignatura ?? c.idAsignatura ?? '') === aid,
      );
      const ejeIds = [
        ...new Set(
          capsAsig.map((c) => String(
            c.IdEjeTematico ?? c.idEjeTematico ?? c.IdUnidad ?? c.idUnidad ?? '0',
          )),
        ),
      ].sort((a, b) => Number(a) - Number(b));

      const ejes = ejeIds.map((eid) => {
        const capsEje = capsAsig
          .filter((c) => String(
            c.IdEjeTematico ?? c.idEjeTematico ?? c.IdUnidad ?? c.idUnidad ?? '0',
          ) === eid)
          .sort((a, b) => (a.Orden ?? 0) - (b.Orden ?? 0) || Number(a.Id) - Number(b.Id));
        return {
          ejeId: eid !== '0' ? eid : null,
          eje: eid !== '0' ? ejePorId[eid] : null,
          capitulos: capsEje,
        };
      });
      return { asignaturaId: aid, asignatura: asigPorId[aid], ejes };
    });

    return { curso: cur, materias };
  });
}

/** Lee el paquete desde legacy (sin escribir en Django). Opcionalmente acotado por curso. */
export async function cargarPaqueteLegacy(colegioId, { idCurso } = {}) {
  const params = {};
  if (idCurso != null && idCurso !== '') params.idCurso = idCurso;
  const { data } = await SyncModules.getPaquete(colegioId, params);
  return enriquecerEjesDesdeCapitulos(data);
}

/** Sync parcial por curso: no reconciliar (evita marcar stale el resto del colegio). */
function esSyncAcotado(paquete, cursoId) {
  return (cursoId != null && cursoId !== '') || paquete?.meta?.alcance === 'curso';
}

/** Resumen de conteos del paquete legacy. */
export function resumirPaquete(paquete) {
  if (!paquete) return null;
  const colPayload = payloadColegioDjango(paquete.colegio);
  return {
    colegio: colPayload.Glosa || paquete.colegio?.Glosa || '—',
    alcance: paquete.meta?.alcance || 'colegio',
    idCurso: paquete.meta?.id_curso ?? null,
    cursos: paquete.cursos?.length ?? paquete.meta?.total_cursos ?? 0,
    asignaturas: paquete.asignaturas?.length ?? paquete.meta?.total_asignaturas ?? 0,
    ejes: paquete.ejes?.length ?? paquete.meta?.total_ejes ?? 0,
    capitulos: paquete.capitulos?.length ?? paquete.meta?.total_capitulos ?? 0,
    comunaLegacy: colPayload.comuna || '—',
    regionLegacy: colPayload.region || '—',
  };
}

/** Estima pasos mínimos de upsert Django (sin alumnos/preguntas dinámicos). */
export function estimarPasosMinimos(paquete, fases) {
  let n = 0;
  if (fases.colegio !== false && paquete?.colegio) n += 2;
  if (fases.cursos !== false) n += paquete?.cursos?.length || 0;
  if (fases.asignaturas !== false) {
    n += paquete?.asignaturas?.length || 0;
    if (paquete?.asignaturas?.length) n += 1;
  }
  if (fases.ejes !== false) {
    n += paquete?.ejes?.length || 0;
    if (paquete?.ejes?.length) n += 1;
  }
  if (fases.capitulos !== false) {
    n += paquete?.capitulos?.length || 0;
    if (paquete?.capitulos?.length) n += 1;
  }
  if (fases.alumnos !== false) n += paquete?.cursos?.length || 0;
  return n;
}

/** Sincroniza asignaturas y ejes del paquete completo (antes del árbol por curso). */
async function sincronizarCatalogoPaquete(ctx) {
  const {
    paquete,
    sesionId,
    cid,
    fases,
    forzarActualizacion,
    doneAsig,
    doneEje,
    bucketAsig,
    bucketEje,
    tick,
    report,
    batchSize,
    signal,
  } = ctx;
  const ejeAsigMap = mapaEjeAsignatura(paquete);

  if (fases.asignaturas !== false) {
    const asigs = (paquete.asignaturas || []).filter((asig) => !doneAsig.has(String(asig.Id)));
    const lista = asigs.map((asig) => ({
      entidad: ENT.ASIGNATURA,
      datos: asig,
      refs: { colegio_id: cid },
      id_externo: asig.Id,
      item: asig,
      asigKey: String(asig.Id),
      fase: 'asignatura',
    }));
    await syncBulkLista({
      sesionId,
      cid,
      forzarActualizacion,
      lista,
      bucket: bucketAsig,
      fase: 'asignatura',
      entidad: ENT.ASIGNATURA,
      tick,
      report,
      batchSize,
      onItemSuccess: (spec) => doneAsig.add(spec.asigKey),
      signal,
    });
  }

  if (fases.ejes !== false) {
    const ejesLista = (paquete.ejes || []).filter((eje) => !doneEje.has(String(eje.Id)));
    const lista = ejesLista.map((eje) => {
      const key = String(eje.Id);
      const asigId = ejeAsigMap[key];
      return {
        entidad: ENT.EJE,
        datos: eje,
        refs: asigId ? { asignatura_id: asigId } : {},
        id_externo: eje.Id,
        item: eje,
        ejeKey: key,
        fase: 'eje',
      };
    });
    await syncBulkLista({
      sesionId,
      cid,
      forzarActualizacion,
      lista,
      bucket: bucketEje,
      fase: 'eje',
      entidad: ENT.EJE,
      tick,
      report,
      batchSize,
      onItemSuccess: (spec) => doneEje.add(spec.ejeKey),
      signal,
    });
    report(
      'eje',
      {
        mensaje: `${paquete.ejes?.length || 0} eje(s) en paquete legacy · ${doneEje.size} procesado(s)`,
      },
      null,
      null,
      { tipo: 'info' },
    );
  }
}

export async function ejecutarSincronizacionCompleta(opts) {
  const {
    colegioId,
    cursoId,
    sesionId,
    fases = {},
    paquete: paqueteIn,
    onProgress = () => {},
    onStatus = () => {},
    batchSize: batchSizeIn,
    delayMs: delayMsIn,
    forzarActualizacion = false,
    modoEventosSync: modoEventosSyncIn,
    signal,
  } = opts;
  const batchSize = normalizarBatchSize(batchSizeIn ?? BATCH_SIZE_SYNC_DEFAULT);
  const delayMs = delayMsIn ?? 0;
  const modoEventosSync = normalizarModoEventosSync(modoEventosSyncIn ?? leerModoEventosSync());
  _syncLogItemsBulk = opts.logItemsBulk ?? logItemsBulkDesdeModo(modoEventosSync);
  const cid = String(colegioId);
  const resumen = { fases: {} };
  let paso = 0;
  let abortada = false;
  let paquete = paqueteIn;

  const tick = (fase, label, entidad = fase) => {
    paso += 1;
    onStatus({
      paso,
      fase,
      entidad,
      label,
      mensaje: label,
    });
  };

  const reportRaw = crearReporter(sesionId, onProgress, modoEventosSync);
  const report = (fase, item, result, error, extra = {}) => {
    reportRaw({ fase, item, result, error, ...extra });
  };

  await persistirEventoSync(sesionId, {
    fase: 'sync',
    tipo: 'info',
    item: { mensaje: `Inicio sync · colegio legacy #${cid}` },
  }, modoEventosSync);

  const bucketCurso = faseVacia();
  const bucketAsig = faseVacia();
  const bucketEje = faseVacia();
  const bucketCap = faseVacia();
  const bucketEst = faseVacia();
  const idsEstudiantes = new Set();
  const idsContratos = new Set();
  const idsDetalleContrato = new Set();

  try {
  assertNotAborted(signal);
  onStatus({ paso: 0, fase: 'legacy', label: 'Obteniendo paquete desde admin C#…', mensaje: 'Leyendo legacy…' });
  report('legacy', { mensaje: 'Consultando SyncDjango/v1/paquete…' }, { mensaje: 'Inicio lectura legacy' }, null, { tipo: 'info' });

  paquete = paqueteIn || (await cargarPaqueteLegacy(colegioId, { idCurso: cursoId }));
  assertNotAborted(signal);
  const acotado = esSyncAcotado(paquete, cursoId);

  const res = resumirPaquete(paquete);
  const alcanceTxt = acotado ? `curso #${res.idCurso || cursoId}` : 'colegio completo';
  report('legacy', { mensaje: `Paquete recibido (${alcanceTxt}): ${res.cursos} cursos, ${res.capitulos} capítulos` }, null, null, { tipo: 'info' });
  onStatus({
    paso: 0,
    fase: 'django',
    label: `Paquete listo · sync hacia Django (lotes secuenciales ×${batchSize})`,
    mensaje: `${alcanceTxt} · ${res.cursos} cursos · ${res.capitulos} capítulos · comuna: ${res.comunaLegacy || '—'}`,
    paquete: res,
    batchSize,
  });

  if (fases.colegio !== false && paquete.colegio) {
    assertNotAborted(signal);
    const bucket = faseVacia();
    tick('colegio', `Colegio ${textoLegacy(paquete.colegio.Glosa, 60)}`, ENT.COLEGIO);
    try {
      const r = await upsertOne(sesionId, ENT.COLEGIO, paquete.colegio, {}, cid, forzarActualizacion);
      contarResultado(bucket, r);
      report('colegio', paquete.colegio, r, null);
    } catch (e) {
      bucket.error += 1;
      report('colegio', paquete.colegio, null, e);
    }
    await delay(delayMs);

    tick('colegio', 'Periodo escolar actual', ENT.PERIODO);
    try {
      const rp = await upsertOne(
        sesionId,
        ENT.PERIODO,
        { nombre: new Date().getFullYear(), es_actual: true, Id: `${cid}-periodo` },
        { colegio_id: cid },
        cid,
        forzarActualizacion,
      );
      contarResultado(bucket, rp);
      report('periodo', { Id: `${cid}-periodo` }, rp, null);
    } catch (e) {
      bucket.error += 1;
      report('periodo', { Id: `${cid}-periodo` }, null, e);
    }
    await delay(delayMs);
    resumen.fases.colegio = bucket;
  }

  const ejePorId = indiceEjes(paquete);
  const arbol = agruparCatalogoPorCurso(paquete);
  const doneAsig = new Set();
  const doneEje = new Set();
  const doneCap = new Set();

  const catalogoActivo =
    fases.cursos !== false
    || fases.asignaturas !== false
    || fases.ejes !== false
    || fases.capitulos !== false
    || fases.preguntas !== false
    || fases.actividades !== false;

  if (catalogoActivo || fases.alumnos !== false) {
    assertNotAborted(signal);
    await sincronizarCatalogoPaquete({
      paquete,
      sesionId,
      cid,
      fases,
      forzarActualizacion,
      doneAsig,
      doneEje,
      bucketAsig,
      bucketEje,
      tick,
      report,
      batchSize,
      signal,
    });
    assertNotAborted(signal);

    if (fases.cursos !== false) {
      const listaCursos = arbol.map((r) => r.curso).map((cur) => ({
        entidad: ENT.CURSO,
        datos: cur,
        refs: { colegio_id: cid },
        id_externo: cur.Id,
        item: cur,
        fase: 'curso',
      }));
      await syncBulkLista({
        sesionId,
        cid,
        forzarActualizacion,
        lista: listaCursos,
        bucket: bucketCurso,
        fase: 'curso',
        entidad: ENT.CURSO,
        tick,
        report,
        batchSize,
        signal,
      });
    }

    // Asignaturas / ejes / capítulos del árbol (pendientes tras catálogo global)
    const asigTree = [];
    const ejeTree = [];
    const capsTree = [];
    for (const rama of arbol) {
      for (const mat of rama.materias) {
        const asig = mat.asignatura;
        const asigKey = mat.asignaturaId;
        if (fases.asignaturas !== false && asig && !doneAsig.has(asigKey)) {
          asigTree.push({ asig, asigKey });
        }
        for (const ej of mat.ejes) {
          const ejeKey = ej.ejeId ? String(ej.ejeId) : '';
          const ejeRow = ej.eje || (ej.ejeId ? { Id: ej.ejeId, Glosa: `Eje ${ej.ejeId}` } : null);
          if (fases.ejes !== false && ejeRow && ejeKey && !doneEje.has(ejeKey)) {
            ejeTree.push({ ejeRow, ejeKey, asig });
          }
          for (const cap of ej.capitulos) {
            const capKey = String(cap.Id);
            if (fases.capitulos !== false && !doneCap.has(capKey)) {
              capsTree.push({ cap, capKey });
            }
          }
        }
      }
    }

    await syncBulkLista({
      sesionId,
      cid,
      forzarActualizacion,
      lista: asigTree.map(({ asig, asigKey }) => ({
        entidad: ENT.ASIGNATURA,
        datos: asig,
        refs: { colegio_id: cid },
        id_externo: asig.Id,
        item: asig,
        asigKey,
        fase: 'asignatura',
      })),
      bucket: bucketAsig,
      fase: 'asignatura',
      entidad: ENT.ASIGNATURA,
      tick,
      report,
      batchSize,
      onItemSuccess: (spec) => doneAsig.add(spec.asigKey),
      signal,
    });

    await syncBulkLista({
      sesionId,
      cid,
      forzarActualizacion,
      lista: ejeTree.map(({ ejeRow, ejeKey, asig }) => ({
        entidad: ENT.EJE,
        datos: ejeRow,
        refs: asig ? { asignatura_id: asig.Id } : {},
        id_externo: ejeRow.Id,
        item: ejeRow,
        ejeKey,
        fase: 'eje',
      })),
      bucket: bucketEje,
      fase: 'eje',
      entidad: ENT.EJE,
      tick,
      report,
      batchSize,
      onItemSuccess: (spec) => doneEje.add(spec.ejeKey),
      signal,
    });

    if (fases.capitulos !== false && capsTree.length) {
      await syncBulkLista({
        sesionId,
        cid,
        forzarActualizacion,
        lista: capsTree.map(({ cap, capKey }) => ({
          entidad: ENT.CAPITULO,
          datos: cap,
          refs: refsCapitulo(cap, ejePorId),
          id_externo: cap.Id,
          item: cap,
          capKey,
          fase: 'capitulo',
        })),
        bucket: bucketCap,
        fase: 'capitulo',
        entidad: ENT.CAPITULO,
        tick,
        report,
        batchSize,
        onItemSuccess: (spec) => doneCap.add(spec.capKey),
        signal,
      });
    }

    if (fases.preguntas !== false || fases.actividades !== false) {
      for (const item of capsTree) {
        const cap = item.cap;
        assertNotAborted(signal);
        const refs = refsCapitulo(cap, ejePorId);

        if (fases.preguntas !== false && refs.eje_id) {
          try {
            const pr = await SyncModules.getPreguntas(refs.eje_id, cap.Id);
            const preguntas = pr.data || [];
            if (preguntas.length) {
              await syncBulkLista({
                sesionId,
                cid,
                forzarActualizacion,
                lista: preguntas.map((preg) => ({
                  entidad: ENT.PREGUNTA,
                  datos: {
                    Id: preg.id_pregunta,
                    enunciado: preg.enunciado,
                    explicacion: preg.solucion,
                  },
                  refs: { capitulo_id: cap.Id },
                  id_externo: preg.id_pregunta,
                  item: preg,
                  fase: 'pregunta',
                })),
                bucket: bucketCap,
                fase: 'pregunta',
                entidad: ENT.PREGUNTA,
                tick,
                report,
                batchSize,
                signal,
              });
              const altLista = [];
              for (const preg of preguntas) {
                for (const alt of preg.alternativas || []) {
                  altLista.push({
                    entidad: ENT.ALTERNATIVA,
                    datos: alt,
                    refs: { pregunta_id: preg.id_pregunta },
                    item: alt,
                    fase: 'alternativa',
                  });
                }
              }
              if (altLista.length) {
                await syncBulkLista({
                  sesionId,
                  cid,
                  forzarActualizacion,
                  lista: altLista,
                  bucket: bucketCap,
                  fase: 'alternativa',
                  entidad: ENT.ALTERNATIVA,
                  tick,
                  report,
                  batchSize,
                  signal,
                });
              }
            }
          } catch (pe) {
            bucketCap.error += 1;
            report('pregunta', cap, null, pe);
          }
        }

        if (fases.actividades !== false) {
          report(
            'actividad',
            cap,
            { mensaje: 'Actividades interactivas: importar o asignar en Django (no vienen del legacy)' },
            null,
            { tipo: 'info' },
          );
        }
      }
    }

    for (const rama of arbol) {
      assertNotAborted(signal);
      const cur = rama.curso;

      if (fases.alumnos !== false) {
        tick('estudiante', `Alumnos curso #${cur.Id} (legacy)`, ENT.ESTUDIANTE);
        const { data: bloque } = await SyncModules.getAlumnosPorCurso(colegioId, cur.Id);
        const alumnos = bloque.alumnos || [];
        report(
          'estudiante',
          { mensaje: `${alumnos.length} alumno(s) en curso ${cur.Id}` },
          null,
          null,
          { tipo: 'info' },
        );

        const secciones = new Map();
        for (const u of alumnos) {
          const casa = cursoCasaAlumno(u);
          if (!casa) continue;
          const key = `${cur.Id}|${casa.toLowerCase()}`;
          if (!secciones.has(key)) {
            secciones.set(key, { idCurso: cur.Id, casa, glosa: cur.Glosa });
          }
        }
        if (secciones.size) {
          const listaSec = [...secciones.values()].map((sec) => ({
            entidad: ENT.CURSO,
            datos: {
              Id: idExternoCursoSeccion(sec.idCurso, sec.casa),
              Glosa: sec.glosa,
              curso_casa: sec.casa,
              casa: sec.casa,
              IdCurso: sec.idCurso,
            },
            refs: {
              colegio_id: cid,
              curso_legacy_id: sec.idCurso,
              glosa_curso: sec.glosa,
            },
            id_externo: idExternoCursoSeccion(sec.idCurso, sec.casa),
            item: sec,
            fase: 'curso',
          }));
          report(
            'curso',
            { mensaje: `${listaSec.length} sección(es) casa/curso en legacy ${cur.Id}` },
            null,
            null,
            { tipo: 'info' },
          );
          await syncBulkLista({
            sesionId,
            cid,
            forzarActualizacion,
            lista: listaSec,
            bucket: bucketCurso,
            fase: 'curso',
            entidad: ENT.CURSO,
            tick,
            report,
            batchSize,
            signal,
          });
        }

        const listaEst = alumnos.map((u) => {
          idsEstudiantes.add(String(u.Id));
          const casa = cursoCasaAlumno(u);
          const idCursoLegacy = u.IdCurso ?? cur.Id;
          const datos = {
            Id: u.Id,
            Nombre: u.Nombre,
            Apellido_paterno: u.Apellido_paterno,
            Apellido_materno: u.Apellido_materno,
            Email: u.Email,
            Rut: u.Rut,
            Vigente: u.Vigente,
            IdCurso: idCursoLegacy,
            Curso_casa: casa,
            curso_casa: casa,
            GlosaCurso: cur.Glosa,
          };
          return {
            entidad: ENT.ESTUDIANTE,
            datos,
            refs: {
              colegio_id: cid,
              curso_id: idCursoLegacy,
              glosa_curso: cur.Glosa,
            },
            id_externo: u.Id,
            item: datos,
            alumno: u,
            fase: 'estudiante',
          };
        });
        await syncBulkLista({
          sesionId,
          cid,
          forzarActualizacion,
          lista: listaEst,
          bucket: bucketEst,
          fase: 'estudiante',
          entidad: ENT.ESTUDIANTE,
          tick,
          report,
          batchSize,
          signal,
        });

        if (fases.contratos !== false) {
          for (const u of alumnos) {
            assertNotAborted(signal);
            const datos = {
              Id: u.Id,
              Nombre: u.Nombre,
              Apellido_paterno: u.Apellido_paterno,
              Apellido_materno: u.Apellido_materno,
              Email: u.Email,
              Rut: u.Rut,
              Vigente: u.Vigente,
              IdCurso: u.IdCurso ?? cur.Id,
            };
            try {
              const { data: ct } = await SyncModules.getContratoAlumno(u.Id);
              if (ct.cabecera) {
                idsContratos.add(String(ct.cabecera.id_contrato));
                tick('contrato', `Contrato alumno #${u.Id}`, ENT.CONTRATO);
                const rc = await upsertOne(
                  sesionId,
                  ENT.CONTRATO,
                  {
                    IdContrato: ct.cabecera.id_contrato,
                    IdUsuario: u.Id,
                    estado: ct.cabecera.vigente ? 'activo' : 'finalizado',
                  },
                  { estudiante_id: u.Id, colegio_id: cid },
                  cid,
                  forzarActualizacion,
                );
                contarResultado(bucketEst, rc);
                report('contrato', datos, rc, null);
              }
              const detLista = (ct.detalles || []).filter((det) => det.id_curso);
              if (detLista.length) {
                await syncBulkLista({
                  sesionId,
                  cid,
                  forzarActualizacion,
                  lista: detLista.map((det) => {
                    if (det.id_contrato_detalle != null) {
                      idsDetalleContrato.add(String(det.id_contrato_detalle));
                    }
                    return {
                      entidad: ENT.DETALLE_CONTRATO,
                      datos: {
                        Id: det.id_contrato_detalle,
                        motivo: det.asignatura || det.curso || '',
                      },
                      refs: {
                        contrato_id: ct.cabecera?.id_contrato,
                        curso_id: det.id_curso,
                      },
                      id_externo: det.id_contrato_detalle,
                      item: det,
                      fase: 'detalle_contrato',
                    };
                  }),
                  bucket: bucketEst,
                  fase: 'detalle_contrato',
                  entidad: ENT.DETALLE_CONTRATO,
                  tick,
                  report,
                  batchSize,
                  signal,
                });
              }
            } catch (ce) {
              bucketEst.error += 1;
              report('contrato', datos, null, ce);
            }
          }
        }
      }
    }

    if (!signal?.aborted && !acotado && (paquete.asignaturas?.length || doneAsig.size)) {
      tick('asignatura', 'Reconciliar materias', ENT.ASIGNATURA);
      try {
        const ids = [...new Set([...(paquete.asignaturas || []).map((a) => a.Id), ...doneAsig])];
        const rec = await reconciliarEntidad(sesionId, ENT.ASIGNATURA, ids, cid);
        bucketAsig.stale = rec.total_stale || 0;
        reportReconciliar(report, 'asignatura', ENT.ASIGNATURA, rec);
      } catch (re) {
        report('asignatura', { reconciliar: true }, null, re);
      }
      await delay(delayMs);
    }

    if (!signal?.aborted && !acotado && (paquete.ejes?.length || doneEje.size)) {
      tick('eje', 'Reconciliar ejes', ENT.EJE);
      try {
        const ids = [...new Set([...(paquete.ejes || []).map((e) => e.Id), ...doneEje])];
        const rec = await reconciliarEntidad(sesionId, ENT.EJE, ids, cid);
        bucketEje.stale = rec.total_stale || 0;
        reportReconciliar(report, 'eje', ENT.EJE, rec);
      } catch (re) {
        report('eje', { reconciliar: true }, null, re);
      }
      await delay(delayMs);
    }

    if (!signal?.aborted && !acotado && (paquete.capitulos?.length || doneCap.size)) {
      tick('capitulo', 'Reconciliar capítulos', ENT.CAPITULO);
      try {
        const ids = [...new Set([...(paquete.capitulos || []).map((c) => c.Id), ...doneCap])];
        const rec = await reconciliarEntidad(sesionId, ENT.CAPITULO, ids, cid);
        bucketCap.stale = rec.total_stale || 0;
        reportReconciliar(report, 'capitulo', ENT.CAPITULO, rec);
      } catch (re) {
        report('capitulo', { reconciliar: true }, null, re);
      }
      await delay(delayMs);
    }
  }

  if (!signal?.aborted && fases.alumnos !== false && (idsEstudiantes.size || bucketEst.ok || bucketEst.error)) {
    if (!acotado && idsEstudiantes.size) {
      tick('estudiante', 'Reconciliar alumnos', ENT.ESTUDIANTE);
      try {
        const rec = await reconciliarEntidad(sesionId, ENT.ESTUDIANTE, [...idsEstudiantes], cid);
        bucketEst.stale = rec.total_stale || 0;
        reportReconciliar(report, 'estudiante', ENT.ESTUDIANTE, rec);
      } catch (re) {
        report('estudiante', { reconciliar: true }, null, re);
      }
    }

    if (!acotado && fases.contratos !== false && idsContratos.size) {
      tick('contrato', 'Reconciliar contratos', ENT.CONTRATO);
      try {
        const rec = await reconciliarEntidad(sesionId, ENT.CONTRATO, [...idsContratos], cid);
        bucketEst.stale_contratos = rec.total_stale || 0;
        reportReconciliar(report, 'contrato', ENT.CONTRATO, rec);
      } catch (re) {
        report('contrato', { reconciliar: true }, null, re);
      }
    }

    if (!acotado && fases.contratos !== false && idsDetalleContrato.size) {
      tick('detalle_contrato', 'Reconciliar detalles contrato', ENT.DETALLE_CONTRATO);
      try {
        const rec = await reconciliarEntidad(
          sesionId,
          ENT.DETALLE_CONTRATO,
          [...idsDetalleContrato],
          cid,
        );
        bucketEst.stale_detalles = rec.total_stale || 0;
        reportReconciliar(report, 'detalle_contrato', ENT.DETALLE_CONTRATO, rec);
      } catch (re) {
        report('detalle_contrato', { reconciliar: true }, null, re);
      }
    }
  }
  } catch (e) {
    if (e instanceof SyncAbortError) {
      abortada = true;
      report(
        'sync',
        { mensaje: 'Sync detenida por el usuario' },
        { mensaje: 'Detenida' },
        null,
        { tipo: 'info' },
      );
    } else {
      throw e;
    }
  }

  if (bucketCurso.ok || bucketCurso.error) resumen.fases.curso = bucketCurso;
  if (bucketAsig.ok || bucketAsig.error) resumen.fases.asignatura = bucketAsig;
  if (bucketEje.ok || bucketEje.error) resumen.fases.eje = bucketEje;
  if (bucketCap.ok || bucketCap.error) resumen.fases.capitulo = bucketCap;
  if (bucketEst.ok || bucketEst.error || idsEstudiantes.size) {
    resumen.fases.estudiante = bucketEst;
  }

  resumen.abortada = abortada;
  onStatus({
    paso,
    fase: 'fin',
    label: abortada ? 'Sync detenida…' : 'Finalizando sesión…',
    mensaje: abortada ? 'Guardando progreso parcial en Django' : 'Sync completada',
  });
  const { data: finalizacion } = await DjangoSync.finalizarSesion(sesionId, resumen);
  await persistirEventoSync(sesionId, {
    fase: 'sync',
    tipo: 'info',
    item: {
      mensaje: abortada
        ? `Sync detenida · ${finalizacion?.errores_total ?? finalizacion?.totales?.error ?? 0} error(es) en log Django`
        : `Sync finalizada · ${finalizacion?.errores_total ?? finalizacion?.totales?.error ?? 0} error(es) en log Django`,
    },
  }, modoEventosSync);
  return { resumen, paquete, pasosEjecutados: paso, finalizacion, abortada, modoEventosSync };
}

export { ENT };
